import React, { useEffect, useRef, useState } from 'react';
import { API, axios } from '../../utils/api';
import { classi, messaggioErrore, useInvioUnico } from '../accesso/campi';
import {
  MINUTI_RICHIAMO_ESITO, leggiData, ora, quandoGiorno, conArticoloLuogo, titoloBreve, luogoBreve,
} from '../../utils/percorso';
import { motiviVisita } from '../../utils/immobili';
import { MOTIVO_CON_NOTA } from '../../utils/percorso';
import { comprimiFoto, MAX_FOTO_VISITA } from '../../utils/foto';
import Icona from './Icone';
import { useTraduzioni, PRINCIPALE } from '../../i18n';
import { useScossone } from './SezioniHome';

// Card "Com'è andata?": dall'orario della visita finché il cliente non risponde, una per visita.
// "Ci piace" (nota facoltativa) oppure "Non fa per noi" (almeno un motivo o una nota); foto facoltative.

const MS_GRAZIE = 1800;
const maiuscola = (testo) => (testo ? testo.charAt(0).toUpperCase() + testo.slice(1) : testo);
const MS_RILETTURA = 3500; // il tempo di leggere "Avete già risposto…" prima che la card sparisca

// Foto già caricate: si leggono con il login (sono private), quindi come blob e non con <img src> diretto
const useFotoServer = (visitaId, foto) => {
  const [url, setUrl] = useState({});
  useEffect(() => {
    let attivo = true;
    const creati = [];
    (foto || []).forEach(async (f) => {
      try {
        const r = await axios.get(`${API}/visite/${visitaId}/foto/${f.id}`, { responseType: 'blob' });
        const u = URL.createObjectURL(r.data);
        creati.push(u);
        if (attivo) setUrl((prima) => ({ ...prima, [f.id]: u }));
      } catch { /* la miniatura resta vuota */ }
    });
    return () => { attivo = false; creati.forEach((u) => URL.revokeObjectURL(u)); };
  }, [visitaId, foto]);
  return url;
};

const FotoVisita = ({ visita, onErrore, children }) => {
  const { t } = useTraduzioni();
  const [foto, setFoto] = useState(() => (visita.foto || []).map((f) => ({ id: f.id, stato: 'ok' })));
  const giaSulServer = useFotoServer(visita.id, visita.foto);
  const input = useRef(null);
  const locali = useRef([]);
  const posti = MAX_FOTO_VISITA - foto.length;

  useEffect(() => () => locali.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const scegli = async (e) => {
    const file = Array.from(e.target.files || []);
    e.target.value = '';
    if (!file.length) return;
    if (file.length > posti) onErrore(t('home.esito.foto_troppe', { massimo: MAX_FOTO_VISITA, quante: posti }));
    for (const originale of file.slice(0, posti)) {
      const chiave = `nuova-${Math.random().toString(36).slice(2)}`;
      // eslint-disable-next-line no-await-in-loop
      const compressa = await comprimiFoto(originale);
      const locale = URL.createObjectURL(compressa);
      locali.current.push(locale);
      setFoto((f) => [...f, { id: chiave, locale, stato: 'carica' }]);
      try {
        const dati = new FormData();
        dati.append('file', compressa, compressa.name || 'foto.jpg');
        // eslint-disable-next-line no-await-in-loop
        const r = await axios.post(`${API}/visite/${visita.id}/foto`, dati);
        setFoto((f) => f.map((x) => (x.id === chiave ? { ...x, id: r.data.id, stato: 'ok' } : x)));
      } catch (err) {
        setFoto((f) => f.filter((x) => x.id !== chiave));
        onErrore(messaggioErrore(err, t('home.esito.foto_non_caricata')));
      }
    }
  };

  // Una richiesta per foto: con il doppio tocco su "×" partivano due DELETE e il secondo diceva "Foto non trovata."
  // Il blocco è subito (ref, prima che React ridisegni) e la "×" resta spenta finché il server non risponde.
  const inRimozione = useRef(new Set());
  const [togliendo, setTogliendo] = useState(() => new Set());
  const togli = async (f) => {
    if (inRimozione.current.has(f.id)) return;
    inRimozione.current.add(f.id);
    setTogliendo(new Set(inRimozione.current));
    try {
      await axios.delete(`${API}/visite/${visita.id}/foto/${f.id}`);
      setFoto((tutte) => tutte.filter((x) => x.id !== f.id));
    } catch (err) {
      // già tolta (da un'altra scheda): sparisce senza errore
      if (err?.response?.status === 404) setFoto((tutte) => tutte.filter((x) => x.id !== f.id));
      else onErrore(messaggioErrore(err, t('home.esito.foto_non_tolta')));
    } finally {
      inRimozione.current.delete(f.id);
      setTogliendo(new Set(inRimozione.current));
    }
  };

  const caricamento = foto.some((f) => f.stato === 'carica');
  return (
    <>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={scegli} data-testid="foto-visita" />
      <button type="button" onClick={() => input.current?.click()} disabled={posti <= 0 || caricamento}>
        ＋ {foto.length ? t('home.esito.foto_altre', { quante: foto.length, massimo: MAX_FOTO_VISITA }) : t('home.esito.foto_aggiungi')}
      </button>
      {children}
      {foto.length > 0 && (
        <div className="miniature">
          {foto.map((f) => (
            <div key={f.id} className={classi('miniatura', f.stato === 'carica' && 'carica')}>
              {(f.locale || giaSulServer[f.id]) && <img src={f.locale || giaSulServer[f.id]} alt="" />}
              {f.stato === 'ok' && (
                <button type="button" onClick={() => togli(f)} disabled={togliendo.has(f.id)} aria-busy={togliendo.has(f.id)} aria-label={t('home.esito.foto_togli')}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const CardEsito = ({ visita, immobile, consulente, adesso, onFatto }) => {
  const { t, l } = useTraduzioni();
  const [scelta, setScelta] = useState(null); // 'positivo' | 'negativo'
  const [motivi, setMotivi] = useState([]);
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(null);
  const [grazie, setGrazie] = useState(null);
  const [ferma, setFerma] = useState(false); // il server ha rifiutato: si aspetta la rilettura della home
  const { scossa, scuoti, fine } = useScossone();
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const data = leggiData(visita.data_visita);
  const giorno = quandoGiorno(data, adesso, l);
  // in italiano il luogo con la preposizione ("in via Manzoni"); nelle altre lingue il luogo nudo, dentro la frase
  const dove = l === PRINCIPALE ? conArticoloLuogo(immobile) : luogoBreve(immobile);
  const titolo = immobile ? titoloBreve(immobile, l) : (visita.immobile_titolo || t('immobili.titolo_generico')).split(',')[0];
  const citta = immobile?.citta || visita.immobile_citta;
  // chi ha accompagnato davvero (visita.con); il consulente solo se manca (prima sempre "con Sandro")
  const chi = visita.con || consulente?.nome;
  const conChi = chi ? ` ${t('home.oggi.con', { nome: chi })}` : '';
  const richiamoInArrivo = adesso - data < MINUTI_RICHIAMO_ESITO * 60000;

  const sbaglio = (testo) => { setErrore(testo); scuoti(); };

  const alterna = (chiave) => {
    setMotivi((m) => (m.includes(chiave) ? m.filter((x) => x !== chiave) : [...m, chiave]));
    setErrore(null);
  };

  // Un invio alla volta (doppio clic, Invio ripetuto con la rete lenta). Se il server rifiuta perché la visita è
  // cambiata (409 "Avete già risposto…" da un'altra scheda, visita annullata: i 4xx) si rileggono i dati della home
  // dopo il messaggio: la card sparisce invece di restare attiva.
  const invio = useInvioUnico();
  const invia = async (e) => {
    e.preventDefault();
    if (!scelta) return; // Invio nella nota prima di scegliere: il bottone non c'è ancora
    if (scelta === 'negativo' && !motivi.length && !nota.trim()) {
      sbaglio(t('home.esito.scegliete_motivo'));
      return;
    }
    // «Altro» da solo non dice niente: o si scrive cos'era, o si sceglie un motivo vero (come lo staff e il server)
    if (scelta === 'negativo' && motivi.includes(MOTIVO_CON_NOTA) && !nota.trim()) {
      sbaglio(t('home.esito.scrivete_altro'));
      return;
    }
    if (!invio.blocca()) return;
    setAttesa(true);
    setErrore(null);
    try {
      await axios.put(`${API}/visite/${visita.id}`, {
        effettuata: true,
        esito_visita: scelta,
        motivi: scelta === 'negativo' ? motivi : [],
        note_esito: nota.trim(),
      });
      setGrazie(scelta === 'positivo'
        ? t('home.esito.grazie_si', { nome: consulente?.nome || maiuscola(t('comune.il_consulente')) })
        : t('home.esito.grazie_no'));
      timer.current = setTimeout(onFatto, MS_GRAZIE);
    } catch (err) {
      sbaglio(messaggioErrore(err, t('home.esito.errore_salva')));
      const stato = err?.response?.status;
      if (stato >= 400 && stato < 500 && stato !== 401) {
        // ferma fino alla rilettura; se dopo la visita aspetta ancora la risposta, si può riprovare
        setFerma(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { setFerma(false); invio.sblocca(); onFatto(); }, MS_RILETTURA);
        return;
      }
      invio.sblocca();
    } finally {
      setAttesa(false);
    }
  };

  return (
    <section className={classi('esito r', scossa && 'shake')} onAnimationEnd={fine} data-testid="card-esito">
      <div className="k">{t('home.esito.fatta', { quando: giorno, ora: ora(data) })}</div>
      <h2>{dove ? t('home.esito.titolo_dove', { dove }) : t('home.esito.titolo')}</h2>
      <p>
        {t('home.esito.dettaglio', { casa: `${titolo}${citta ? `, ${citta}` : ''}`, quando: giorno, ora: ora(data), con: conChi })}
        {' '}{t('home.esito.invito')}
      </p>
      {grazie ? (
        <div className="grazie" role="status"><Icona nome="si" />{grazie}</div>
      ) : (
        <form onSubmit={invia}>
          <div className="grid2">
            <button type="button" className={classi('big si', scelta === 'positivo' && 'on')} aria-pressed={scelta === 'positivo'}
              onClick={() => { setScelta('positivo'); setErrore(null); }}>
              <Icona nome="si" /><div><b>{t('home.esito.ci_piace')}</b><span>{t('home.esito.ci_piace_sotto')}</span></div>
            </button>
            <button type="button" className={classi('big no', scelta === 'negativo' && 'on')} aria-pressed={scelta === 'negativo'}
              onClick={() => { setScelta('negativo'); setErrore(null); }}>
              <Icona nome="no" /><div><b>{t('home.esito.non_fa')}</b><span>{t('home.esito.non_fa_sotto')}</span></div>
            </button>
          </div>
          {scelta === 'negativo' && (
            <div className="why" role="group" aria-label={t('home.esito.gruppo_motivi')}>
              {motiviVisita(l).map(([chiave, etichetta]) => (
                <button type="button" key={chiave} className={motivi.includes(chiave) ? 'on' : undefined}
                  aria-pressed={motivi.includes(chiave)} onClick={() => alterna(chiave)}>{etichetta}</button>
              ))}
            </div>
          )}
          {scelta && (
            <div className="note">
              <input value={nota} onChange={(ev) => { setNota(ev.target.value); setErrore(null); }} maxLength={1000}
                aria-label={t(scelta === 'positivo' ? 'home.esito.nota_si' : 'home.esito.nota_no')}
                placeholder={t(scelta === 'positivo' ? 'home.esito.nota_si_segnaposto' : 'home.esito.nota_no')} />
              <button type="submit" className={classi('btn p sm', attesa && 'busy')} aria-busy={attesa} disabled={attesa || ferma}>{t('comune.invia')}</button>
            </div>
          )}
          {errore && <div className="err" role="alert">{errore}</div>}
          <div className="foto">
            <FotoVisita visita={visita} onErrore={sbaglio}>
              {richiamoInArrivo && <span>{t('home.esito.promemoria')}</span>}
            </FotoVisita>
          </div>
        </form>
      )}
    </section>
  );
};

export default CardEsito;
